import React from "react";
import { motion } from "framer-motion";
import { Check, AlertTriangle, FlaskConical } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import { ServiceHero, SectionHeading } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";
import { LeadForm } from "../components/site/LeadForm";
import { AUDIT_INCLUDES } from "../data/content";

export default function SafetyAudit() {
  return (
    <div data-testid="audit-page">
      <Seo title="FMCSA Safety Audit Preparation for Trucking Companies" description="Standard safety audit preparation for $300 with ~1 week turnaround. Driver qualification files, HOS, vehicle and maintenance records, and a complete document checklist to get audit-ready." />
      <ServiceHero eyebrow="Safety Audit Preparation" title="DON'T WAIT FOR THE AUDIT" highlight="TO GET READY."
        subtitle="Standard Safety Audit Preparation — approximately 1 week turnaround. We review your records, organize your files, and identify what's missing before an auditor ever asks."
        image={IMAGES.parkedFleet} price="STANDARD SAFETY AUDIT PREP — $300" />

      {/* Animated checklist */}
      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="What we review" center className="mb-12">YOUR AUDIT <span className="nm-gold-text">CHECKLIST.</span></SectionHeading>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {AUDIT_INCLUDES.map((it, i) => (
              <motion.div key={it} initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 nm-card rounded-lg p-3.5" data-testid={`audit-item-${i}`}>
                <motion.span initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.05 + 0.15, type: "spring" }}
                  className="w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center shrink-0">
                  <Check size={14} className="text-white" strokeWidth={3} />
                </motion.span>
                <span className="text-gray-200 text-sm">{it}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Extended + CDL */}
      <section className="relative py-16 bg-[#0B132B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="nm-card rounded-2xl p-8 h-full border-l-4 border-[#F59E0B]">
              <AlertTriangle className="text-[#F59E0B] mb-4" size={26} />
              <h3 className="font-head text-xl font-bold text-white mb-1">EXTENDED COMPLIANCE RECONSTRUCTION</h3>
              <p className="nm-gold-text font-head text-lg font-bold mb-3">Starting at $500</p>
              <p className="text-gray-400 text-sm leading-relaxed">
                Carriers whose records have not been maintained or organized may require additional reconstruction and compliance work to get fully audit-ready.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="nm-card rounded-2xl p-8 h-full border-l-4 border-[#2563EB]">
              <FlaskConical className="text-[#3B82F6] mb-4" size={26} />
              <h3 className="font-head text-xl font-bold text-white mb-3">CDL / DRUG & ALCOHOL COMPLIANCE</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Companies operating vehicles requiring a CDL may have additional federal drug and alcohol testing requirements. When applicable,
                we can help connect your company with an appropriate drug and alcohol testing/consortium program.
              </p>
              <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
                Third-party enrollment and testing fees are paid separately by the carrier. Foley enrollment/program costs are separate from Next Mile
                Compliance service fees and are paid directly to the third-party provider.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="Get audit-ready" center className="mb-8">PREPARE FOR <span className="nm-gold-text">MY AUDIT.</span></SectionHeading>
          <LeadForm source="audit" defaultService="Safety Audit Preparation" submitLabel="PREPARE FOR MY AUDIT" />
        </div>
      </section>

      <CTABand eyebrow="Audit coming? Get prepared." title="Don't wait for the audit to get ready." button="PREPARE FOR MY AUDIT" />
    </div>
  );
}
